import React, { useState } from 'react';
import { Modal } from '@attentive-platform/stem-ui';

import { ExampleCardsStyles as S } from './styles';

const isImageType = type => String(type || '').toLowerCase() === 'image';

/** Normalize API `urls` to `{ url, type }` (also accepts legacy string urls). */
export const normalizeExampleUrls = urls =>
  (urls || [])
    .map(entry => {
      if (!entry) return null;
      if (typeof entry === 'string') {
        return { url: entry, type: 'link' };
      }
      const url = entry.url || entry.href;
      if (!url) return null;
      return { url, type: String(entry.type || 'link').toLowerCase() };
    })
    .filter(Boolean);

const DefaultSampleTakeoff = () => (
  <S.DefaultArt aria-hidden>
    <svg viewBox='0 0 24 24' width='48' height='48' fill='currentColor'>
      <path d='M12.4142 5H21C21.5523 5 22 5.44772 22 6V20C22 20.5523 21.5523 21 21 21H3C2.44772 21 2 20.5523 2 20V4C2 3.44772 2.44772 3 3 3H10.4142L12.4142 5ZM4 5V19H20V7H11.5858L9.58579 5H4ZM11 12C11 13.1046 10.1046 14 9 14C7.89543 14 7 13.1046 7 12C7 10.8954 7.89543 10 9 10C10.1046 10 11 10.8954 11 12ZM17 18H7L9.5 14.5L11 16.5L14 12.5L17 18Z' />
    </svg>
  </S.DefaultArt>
);

const ExampleCard = ({ title, caption, type, imageUrl, onOpen }) => {
  const showImage = isImageType(type) && !!imageUrl;
  return (
    <S.Card type='button' onClick={onOpen} title={caption}>
      <S.Thumb>{showImage ? <S.ThumbImg src={imageUrl} alt={title} /> : <DefaultSampleTakeoff />}</S.Thumb>
      <S.CardBody>
        <S.CardTitle>{title}</S.CardTitle>
        <S.CardCaption>{caption}</S.CardCaption>
      </S.CardBody>
    </S.Card>
  );
};

/**
 * Example modifiers: each `urls[]` entry is `{ url, type }`.
 * - type `image` → show image thumbnail; click opens modal
 * - type `link` (or other) → show sample takeoff art; click opens url in new tab
 */
const ExampleCards = ({ modifiers, showHeading = true }) => {
  const [preview, setPreview] = useState(null);

  const cards = (modifiers || []).flatMap(m => {
    const entries = normalizeExampleUrls(m.urls);
    const baseTitle = m.title || 'Sample takeoff';

    if (!entries.length) return [];

    return entries.map((entry, i) => {
      const isImage = isImageType(entry.type);
      return {
        key: `${m.id}-${i}`,
        title: isImage ? baseTitle : 'Sample takeoff',
        caption: isImage ? 'Open in viewer' : 'Open',
        type: entry.type,
        url: entry.url,
        imageUrl: isImage ? entry.url : null
      };
    });
  });

  if (!cards.length) return null;

  const handleOpen = card => {
    if (!card.url) return;
    if (isImageType(card.type)) {
      setPreview(card);
      return;
    }
    window.open(card.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <S.Section>
        {showHeading && <S.Heading>Examples</S.Heading>}
        <S.Grid>
          {cards.map(card => (
            <ExampleCard
              key={card.key}
              title={card.title}
              caption={card.caption}
              type={card.type}
              imageUrl={card.imageUrl}
              onOpen={() => handleOpen(card)}
            />
          ))}
        </S.Grid>
      </S.Section>

      <Modal
        show={!!preview}
        onClose={() => setPreview(null)}
        heading={<span className='pl-4'>{preview?.title || 'Example'}</span>}
        content={
          <S.ModalBody>
            {preview?.url ? (
              <S.ModalImg src={preview.url} alt={preview.title} />
            ) : (
              <S.ModalDefault>
                <DefaultSampleTakeoff />
              </S.ModalDefault>
            )}
          </S.ModalBody>
        }
      />
    </>
  );
};

export default ExampleCards;
